import React, { useCallback, useMemo } from 'react';
import { SearchType } from '../../constants';
import { useInputContext } from '../../context/InputContext';
import DefaultSuggestion from '../../contracts/DefaultSuggestion';
import DefaultSuggestionClicked from '../../contracts/DefaultSuggestionClicked';
import useGetSuggestionHref from '../../hooks/useGetSuggestionHref';
import { useSearchBarContext } from '../../SearchBarContext';
import { encodeAndStringify, generateUrlWithFilters } from '../../utils';

type OwnProps = {
  defaultSuggestion: DefaultSuggestion;
  handleDefaultSuggestionClicked: DefaultSuggestionClicked;
};

export type SuggestionLinkProps = React.PropsWithChildren<
  OwnProps & React.AnchorHTMLAttributes<HTMLAnchorElement>
>;

export default function SuggestionLink({
  children,
  defaultSuggestion,
  handleDefaultSuggestionClicked,
  onClick,
  ...props
}: SuggestionLinkProps) {
  const { inputValue, noResults } = useInputContext();
  const { customEventData, onSuggestionSelection } = useSearchBarContext();
  const getSuggestionUrl = useGetSuggestionHref();

  const eventData = useMemo(
    () => ({
      ...customEventData,
      buildKey: null,
      suggestionIndex: null,
      suggestionsLength: noResults ? 0 : null,
      suggestionText: defaultSuggestion?.text ?? '',
      textEntered: inputValue,
      textSource: SearchType.DefaultSuggestion
    }),
    [customEventData, defaultSuggestion?.text, inputValue, noResults]
  );

  const href = useMemo(
    () =>
      props.href ??
      getSuggestionUrl(
        generateUrlWithFilters(defaultSuggestion.filters),
        eventData
      ),
    [defaultSuggestion.filters, eventData, getSuggestionUrl, props.href]
  );

  const handleOnClick: React.MouseEventHandler<HTMLAnchorElement> = useCallback(
    e => {
      handleDefaultSuggestionClicked(e, defaultSuggestion?.text);
      if (onSuggestionSelection) {
        e.preventDefault();
        onClick?.(e);
        onSuggestionSelection({
          cvnaId: encodeAndStringify(defaultSuggestion?.filters),
          url: href,
          eventData
        });
      }
    },
    [
      defaultSuggestion?.filters,
      defaultSuggestion?.text,
      eventData,
      handleDefaultSuggestionClicked,
      href,
      onClick,
      onSuggestionSelection
    ]
  );

  return (
    // eslint-disable-next-line local-rules/on-click-analytics-id
    <a
      {...props}
      href={href}
      onClick={handleOnClick}
      data-native-included="false"
    >
      {children}
    </a>
  );
}
